// src/app/core/ui/theme/LanguageProvider.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, type TranslationKeys } from './locales';
import { usePlatform } from '@/app/core/environment/PlatformContext';

type Language = 'ru' | 'en';

// Значение словаря для конкретного ключа — из ru (эталон)
type TranslationValue<K extends TranslationKeys> = (typeof translations)['ru'][K];

// Только те ключи, чьи значения — строки. Массивы (newsItems, faqItems, galleryExamples…)
// и объекты сюда не попадают, поэтому t() не может вернуть NewsItem[].
type StringTranslationKeys = {
  [K in TranslationKeys]: TranslationValue<K> extends string ? K : never;
}[TranslationKeys];

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  /** Строковый перевод. Возвращает string. */
  t: <K extends StringTranslationKeys>(key: K) => string;
  /** Сырое значение словаря: для массивов и объектов (newsItems, faqItems, …). */
  tRaw: <K extends TranslationKeys>(key: K) => TranslationValue<K>;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isTelegram } = usePlatform();

  const getAutoLanguage = (): Language => {
    if (isTelegram) {
      const code = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
      if (code === 'ru') return 'ru';
      return 'en';
    }
    const navLang = navigator.language;
    return navLang.startsWith('ru') ? 'ru' : 'en';
  };

  const [lang, setLangState] = useState<Language>(() => {
    const saved = localStorage.getItem('app-language');
    return (saved as Language) || getAutoLanguage();
  });

  useEffect(() => {
    localStorage.setItem('app-language', lang);
  }, [lang]);

  const setLang = useCallback((newLang: Language) => {
    setLangState(newLang);
  }, []);

  const t = useCallback(<K extends StringTranslationKeys>(key: K): string => {
    const value = translations[lang]?.[key as TranslationKeys];
    return typeof value === 'string' ? value : String(key);
  }, [lang]);

  const tRaw = useCallback(<K extends TranslationKeys>(key: K): TranslationValue<K> => {
    const value = translations[lang]?.[key];
    return (value ?? key) as TranslationValue<K>;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tRaw }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useTranslation must be used within LanguageProvider');
  return ctx;
};